import React, { useEffect, useMemo, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import api from "@/lib/api";
import { toast } from "sonner";
import { ScanLine, CheckCircle2, XCircle, AlertCircle, Ticket, Layers, Users, X, RotateCcw } from "lucide-react";

export default function KioskMarathonScan() {
  const [showtimes, setShowtimes] = useState([]);
  const [showtimeId, setShowtimeId] = useState("");
  const [scanning, setScanning] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);  // local session scan log
  const scannerRef = useRef(null);
  const lastCodeRef = useRef("");
  const lastTimeRef = useRef(0);

  const activeShow = useMemo(() => showtimes.find((s) => s.id === showtimeId), [showtimes, showtimeId]);

  useEffect(() => {
    api.get("/showtimes").then((r) => {
      const today = new Date().toISOString().slice(0, 10);
      const upcoming = r.data
        .filter((s) => s.date >= today && s.status !== "abgesagt")
        .sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time));
      setShowtimes(upcoming);
      // Default: nächste kommende Show
      if (upcoming[0]) setShowtimeId(upcoming[0].id);
    }).catch(() => toast.error("Vorstellungen konnten nicht geladen werden"));
  }, []);

  const submitCode = async (code) => {
    if (!code || !showtimeId) return;
    // Debounce: gleicher Code innerhalb 2s ignorieren
    const now = Date.now();
    if (lastCodeRef.current === code && now - lastTimeRef.current < 2000) return;
    lastCodeRef.current = code; lastTimeRef.current = now;

    try {
      const { data } = await api.post(`/admin/marathon/${encodeURIComponent(code.trim())}/scan`, { showtime_id: showtimeId });
      setResult({ ...data, code: code.trim(), when: new Date().toISOString() });
      setHistory((h) => [{ code: code.trim(), status: data.status, guest: data.guest_name, when: new Date().toISOString() }, ...h].slice(0, 20));
      // Ton (nicht blockierend)
      try {
        new Audio(data.status === "admitted" ? "data:audio/wav;base64,UklGRl4CAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YToCAAA=" : "").play().catch(() => {});
      } catch { /* audio not critical */ }
    } catch (e) {
      const msg = e.response?.data?.detail || "Scan-Fehler";
      setResult({ status: "error", message: msg, code: code.trim() });
      setHistory((h) => [{ code: code.trim(), status: "error", message: msg, when: new Date().toISOString() }, ...h].slice(0, 20));
    }
  };

  const startScanner = async () => {
    if (scanning) return;
    if (!showtimeId) return toast.error("Bitte Vorstellung wählen");
    try {
      const cam = new Html5Qrcode("marathon-qr-reader");
      scannerRef.current = cam;
      await cam.start({ facingMode: "environment" }, { fps: 12, qrbox: { width: 260, height: 260 } },
        (decoded) => {
          // Extract code aus URL wenn nötig (unser QR encoded ist /marathon/{code})
          const m = decoded.match(/marathon\/([A-Za-z0-9-]+)/);
          submitCode(m ? m[1] : decoded);
        },
        () => { /* frame decode failure */ }
      );
      setScanning(true);
    } catch {
      toast.error("Kamera konnte nicht gestartet werden. Manuell eintippen.");
    }
  };
  const stopScanner = async () => {
    try {
      if (scannerRef.current) { await scannerRef.current.stop(); await scannerRef.current.clear(); }
    } catch { /* already stopped */ }
    scannerRef.current = null;
    setScanning(false);
  };
  useEffect(() => () => { stopScanner(); }, []);

  const submitManual = (e) => {
    e.preventDefault();
    if (!manualCode.trim()) return;
    submitCode(manualCode);
    setManualCode("");
  };

  const isAdmitted = result?.status === "admitted";
  const isDuplicate = result?.status === "duplicate";
  const isError = result?.status === "error" || !result?.status;

  return (
    <div className="min-h-screen bg-[#050505] text-[#F5F0E6] p-6" data-testid="marathon-scan-page">
      <div className="max-w-6xl mx-auto space-y-5">
        {/* Header + Show-Selector */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <ScanLine size={26} className="text-[#D4AF37]" />
            <div>
              <h1 className="font-heading text-3xl">Marathon-Einlass</h1>
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#A8A398] mt-1">QR scannen oder Code eintippen</div>
            </div>
          </div>
          <div className="ml-auto min-w-[280px]">
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Aktive Vorstellung</label>
            <select value={showtimeId} onChange={(e) => setShowtimeId(e.target.value)} data-testid="marathon-scan-showtime"
              className="w-full mt-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none">
              <option value="">— Auswählen —</option>
              {showtimes.map((s) => {
                const label = `${(s.date || "").split("-").reverse().join(".")} · ${s.start_time} · ${s.hall}`;
                return <option key={s.id} value={s.id}>{label}</option>;
              })}
            </select>
          </div>
        </div>

        <div className="grid lg:grid-cols-[380px_1fr] gap-5">
          {/* Scanner + Manueller Code */}
          <div className="space-y-4">
            <div className="card-lux p-4">
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-3">QR-Scanner</div>
              <div id="marathon-qr-reader" data-testid="marathon-scanner"
                className="w-full aspect-square bg-[#0A0A0A] border border-[#1A1A1A] rounded overflow-hidden" />
              <div className="mt-3 flex gap-2">
                {!scanning ? (
                  <button onClick={startScanner} data-testid="marathon-scan-start"
                    className="flex-1 py-2.5 bg-[#D4AF37] hover:bg-[#EEDC82] text-black rounded text-[11px] uppercase tracking-widest inline-flex items-center justify-center gap-2">
                    <ScanLine size={13} /> Scanner starten
                  </button>
                ) : (
                  <button onClick={stopScanner} data-testid="marathon-scan-stop"
                    className="flex-1 py-2.5 bg-[#7A1921] hover:bg-[#8f2129] text-white rounded text-[11px] uppercase tracking-widest inline-flex items-center justify-center gap-2">
                    <XCircle size={13} /> Stoppen
                  </button>
                )}
              </div>
            </div>

            <div className="card-lux p-4">
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-3">Manuell eingeben</div>
              <form onSubmit={submitManual} className="flex gap-2">
                <input value={manualCode} onChange={(e) => setManualCode(e.target.value.toUpperCase())}
                  data-testid="marathon-manual-input" placeholder="MAR-XXXXXXXX"
                  className="flex-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2.5 font-mono focus:border-[#D4AF37] outline-none" />
                <button type="submit" data-testid="marathon-manual-submit"
                  className="px-4 bg-[#D4AF37] hover:bg-[#EEDC82] text-black rounded text-[11px] uppercase tracking-widest">
                  Prüfen
                </button>
              </form>
            </div>

            {history.length > 0 && (
              <div className="card-lux p-4">
                <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-3">Letzte Scans · Session</div>
                <div className="space-y-1 max-h-64 overflow-y-auto" data-testid="marathon-scan-history">
                  {history.map((h, i) => (
                    <div key={i} className={`text-xs px-2 py-1.5 rounded flex items-center gap-2 ${
                      h.status === "admitted" ? "bg-[#8FBC8F]/10 text-[#B7E1B7]" :
                      h.status === "duplicate" ? "bg-[#D4AF37]/10 text-[#EEDC82]" :
                      "bg-[#7A1921]/10 text-[#EE9CA1]"
                    }`}>
                      {h.status === "admitted" ? <CheckCircle2 size={11} /> : h.status === "duplicate" ? <AlertCircle size={11} /> : <XCircle size={11} />}
                      <span className="font-mono">{h.code}</span>
                      {h.guest && <span className="opacity-70">· {h.guest}</span>}
                      <span className="ml-auto text-[9px] font-mono opacity-50">{new Date(h.when).toLocaleTimeString("de-DE").slice(0, 5)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Ergebnis-Banner */}
          <div className="min-h-[400px]">
            {!result && (
              <div className="card-lux p-10 h-full flex flex-col items-center justify-center text-center" data-testid="marathon-scan-idle">
                <ScanLine size={72} className="text-[#575148] mb-4" />
                <div className="text-lg text-[#A8A398]">Bereit zum Scannen</div>
                <div className="text-xs text-[#575148] mt-2">Scanner starten oder Code eingeben</div>
                {activeShow && (
                  <div className="mt-8 text-xs text-[#A8A398]">
                    <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-2">Aktive Vorstellung</div>
                    <div className="font-heading text-lg text-[#EEDC82]">{activeShow.movie?.title_de || "Film"}</div>
                    <div className="font-mono mt-1">{(activeShow.date || "").split("-").reverse().join(".")} · {activeShow.start_time} · {activeShow.hall}</div>
                  </div>
                )}
              </div>
            )}

            {isAdmitted && (
              <div className="rounded-lg overflow-hidden shadow-2xl" data-testid="marathon-scan-admitted"
                style={{ background: "linear-gradient(135deg, #1a3d1a 0%, #0F2A0F 100%)", border: "2px solid #8FBC8F" }}>
                <div className="p-8 md:p-12 text-center">
                  <CheckCircle2 size={96} className="text-[#8FBC8F] mx-auto mb-4 animate-pulse" />
                  <div className="text-[10px] uppercase tracking-[0.5em] text-[#B7E1B7] font-heading">Einlass gewährt</div>
                  <div className="font-heading text-5xl md:text-6xl text-[#F5F0E6] mt-3">{result.guest_name}</div>
                  <div className="text-lg text-[#B7E1B7] mt-4 flex items-center justify-center gap-2">
                    <Layers size={16} /> {result.series_name}
                  </div>
                  <div className="mt-6 flex justify-center gap-6 text-sm">
                    <div><Users size={16} className="inline text-[#B7E1B7] mr-1" /> <b className="text-2xl">{result.seat_count}</b> Sitzplätze</div>
                    <div><Ticket size={16} className="inline text-[#B7E1B7] mr-1" /> Noch <b className="text-2xl">{result.remaining}</b> Film{result.remaining === 1 ? "" : "e"}</div>
                  </div>
                  <div className="text-[10px] font-mono text-[#B7E1B7] mt-6 opacity-60">{result.code}</div>
                </div>
                <button onClick={() => setResult(null)} data-testid="marathon-scan-clear"
                  className="w-full py-3 border-t border-[#8FBC8F]/30 text-[#B7E1B7] hover:bg-black/20 text-[10px] uppercase tracking-widest inline-flex items-center justify-center gap-2">
                  <RotateCcw size={12} /> Nächsten Gast scannen
                </button>
              </div>
            )}

            {isDuplicate && (
              <div className="rounded-lg overflow-hidden" data-testid="marathon-scan-duplicate"
                style={{ background: "linear-gradient(135deg, #4a3d1a 0%, #2A2610 100%)", border: "2px solid #D4AF37" }}>
                <div className="p-8 md:p-12 text-center">
                  <AlertCircle size={96} className="text-[#D4AF37] mx-auto mb-4" />
                  <div className="text-[10px] uppercase tracking-[0.5em] text-[#EEDC82] font-heading">Bereits eingelassen</div>
                  <div className="font-heading text-3xl text-[#F5F0E6] mt-3">Dieser Code wurde für diese Vorstellung schon verwendet</div>
                  <div className="text-[10px] font-mono text-[#EEDC82] mt-6 opacity-70">{result.code}</div>
                </div>
                <button onClick={() => setResult(null)} className="w-full py-3 border-t border-[#D4AF37]/30 text-[#EEDC82] hover:bg-black/20 text-[10px] uppercase tracking-widest">
                  OK
                </button>
              </div>
            )}

            {isError && result && (
              <div className="rounded-lg overflow-hidden" data-testid="marathon-scan-error"
                style={{ background: "linear-gradient(135deg, #4a1a1a 0%, #2A1010 100%)", border: "2px solid #C0392B" }}>
                <div className="p-8 md:p-12 text-center">
                  <XCircle size={96} className="text-[#EE9CA1] mx-auto mb-4" />
                  <div className="text-[10px] uppercase tracking-[0.5em] text-[#EE9CA1] font-heading">Zutritt verweigert</div>
                  <div className="font-heading text-2xl text-[#F5F0E6] mt-3">{result.message || "Ungültiger Code"}</div>
                  <div className="text-[10px] font-mono text-[#EE9CA1] mt-6 opacity-70">{result.code}</div>
                </div>
                <button onClick={() => setResult(null)} className="w-full py-3 border-t border-[#C0392B]/30 text-[#EE9CA1] hover:bg-black/20 text-[10px] uppercase tracking-widest">
                  Nochmal versuchen
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
